import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  Building2,
  CheckCircle2,
  CreditCard,
  Database,
  HardDrive,
  Plus,
  Server,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { api } from '@/lib/api';
import { formatBytes, formatCurrency, formatNumber } from '@/lib/format';

export const Route = createFileRoute('/_authed/dashboard')({
  component: DashboardPage,
});

interface OverviewDto {
  totalUsers: number;
  totalSites: number;
  activeSubscriptions: number;
  mrr: number;
  churn: number;
  totalBandwidth: number;
  totalStorage: number;
  resourceUsage: {
    cpu: number;
    memory: number;
    r2: number;
    d1: number;
  };
  seriesVisits: Array<{ date: string; value: number }>;
  seriesRevenue: Array<{ date: string; value: number }>;
  topPlans: Array<{ plan: string; value: number }>;
}

const mock: OverviewDto = {
  totalUsers: 1284,
  totalSites: 312,
  activeSubscriptions: 271,
  mrr: 18920,
  churn: 2.1,
  totalBandwidth: 1.2e12,
  totalStorage: 4.5e10,
  resourceUsage: { cpu: 42, memory: 61, r2: 38, d1: 19 },
  seriesVisits: Array.from({ length: 14 }, (_, i) => ({
    date: `D${i + 1}`,
    value: 800 + Math.round(Math.sin(i / 2) * 220 + Math.random() * 80),
  })),
  seriesRevenue: Array.from({ length: 12 }, (_, i) => ({
    date: `M${i + 1}`,
    value: 12000 + Math.round(Math.cos(i / 2) * 3000 + Math.random() * 1200),
  })),
  topPlans: [
    { plan: 'FREE', value: 120 },
    { plan: 'SILVER', value: 86 },
    { plan: 'GOLD', value: 73 },
    { plan: 'PLATINUM', value: 33 },
  ],
};

function StatCard({
  label,
  value,
  delta,
  icon: Icon,
  loading,
}: {
  label: string;
  value: string;
  delta?: { value: number; positive: boolean };
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {delta && (
          <p className="mt-1 flex items-center text-xs text-muted-foreground">
            <TrendingUp
              className={
                delta.positive
                  ? 'mr-1 h-3 w-3 text-success'
                  : 'mr-1 h-3 w-3 rotate-180 text-destructive'
              }
              aria-hidden
            />
            <span
              className={delta.positive ? 'text-success' : 'text-destructive'}
            >
              {delta.positive ? '+' : ''}
              {delta.value}%
            </span>
            <span className="ml-1">vs mes anterior</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<OverviewDto>({
    queryKey: ['central-overview'],
    queryFn: async () => {
      try {
        return await api.get<OverviewDto>('/admin/overview');
      } catch {
        return mock;
      }
    },
    staleTime: 60 * 1000,
  });
  const d = data ?? mock;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('nav.dashboard')}</h1>
          <p className="text-muted-foreground">
            Resumen operativo de la plataforma
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to="/sites" search={{ new: 1 }}>
              <Plus className="mr-2 h-4 w-4" />
              {t('sites.create')}
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Sitios activos"
          value={formatNumber(d.totalSites)}
          delta={{ value: 4.2, positive: true }}
          icon={Building2}
          loading={isLoading}
        />
        <StatCard
          label="Usuarios totales"
          value={formatNumber(d.totalUsers)}
          delta={{ value: 8.1, positive: true }}
          icon={Users}
          loading={isLoading}
        />
        <StatCard
          label="MRR"
          value={formatCurrency(d.mrr)}
          delta={{ value: 12.4, positive: true }}
          icon={CreditCard}
          loading={isLoading}
        />
        <StatCard
          label="Churn"
          value={`${d.churn.toFixed(1)}%`}
          delta={{ value: 0.3, positive: false }}
          icon={Activity}
          loading={isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Visitas (14 días)</CardTitle>
            <CardDescription>Tráfico agregado de todos los sitios</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.seriesVisits}>
                  <defs>
                    <linearGradient id="visits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(199 89% 48%)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(199 89% 48%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(199 89% 48%)"
                    fill="url(#visits)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Distribución de planes</CardTitle>
            <CardDescription>Suscripciones activas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={d.topPlans}
                    dataKey="value"
                    nameKey="plan"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {d.topPlans.map((_, i) => (
                      <Cell
                        key={i}
                        fill={
                          ['#0ea5e9', '#6366f1', '#a855f7', '#f59e0b'][i % 4]
                        }
                      />
                    ))}
                  </Pie>
                  <Legend />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recursos del edge</CardTitle>
            <CardDescription>Uso agregado de los workers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ResourceBar icon={Server} label="CPU" value={d.resourceUsage.cpu} />
            <ResourceBar
              icon={HardDrive}
              label="Memoria"
              value={d.resourceUsage.memory}
            />
            <ResourceBar icon={Database} label="R2" value={d.resourceUsage.r2} />
            <ResourceBar icon={Database} label="D1" value={d.resourceUsage.d1} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Ingresos recurrentes (12 meses)</CardTitle>
            <CardDescription>MRR en EUR</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.seriesRevenue}>
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="hsl(199 89% 48%)"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Ancho de banda total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(d.totalBandwidth)}</div>
            <p className="text-xs text-muted-foreground">Últimos 30 días</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Almacenamiento usado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(d.totalStorage)}</div>
            <p className="text-xs text-muted-foreground">R2 + KV</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Suscripciones activas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(d.activeSubscriptions)}
            </div>
            <p className="text-xs text-muted-foreground">
              {Math.round((d.activeSubscriptions / d.totalUsers) * 100)}% de los
              usuarios
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ResourceBar({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
}) {
  const status = value > 80 ? 'destructive' : value > 60 ? 'warning' : 'success';
  const IconEnd = value > 80 ? AlertCircle : CheckCircle2;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </span>
        <span className="flex items-center gap-1 font-medium">
          <IconEnd
            className={
              status === 'destructive'
                ? 'h-3 w-3 text-destructive'
                : status === 'warning'
                  ? 'h-3 w-3 text-warning'
                  : 'h-3 w-3 text-success'
            }
          />
          {value}%
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-muted">
        <div
          className={
            status === 'destructive'
              ? 'h-2 rounded-full bg-destructive transition-all'
              : status === 'warning'
                ? 'h-2 rounded-full bg-warning transition-all'
                : 'h-2 rounded-full bg-primary transition-all'
          }
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}
